import { z } from 'zod';
import { AiError } from '../constants/ai-error';
import { AiException } from '../exceptions/ai-exception';
import { OPENROUTER_PROVIDER } from '../types/model-type';

const DECISIONS_URL = 'https://openrouter.ai/api/alpha/decisions';
const DEFAULT_MODEL = '~typesafe/jev-latest';
const DEFAULT_TIMEOUT_MS = 10_000;
const BODY_LIMIT = 500;

/**
 * A Jev question. `choice` criteria is a map of OPTION LABELS to short rubrics,
 * not a free-form scoring guide. A wrong criteria shape makes Jev pick the
 * criteria key instead of an option label. Every option label must appear in
 * `criteria`; an empty-string rubric is acceptable.
 */
export type JevChoiceQuestion = {
  type: 'choice';
  instructions: string;
  criteria: Record<string, string>;
  options: { label: string; description: string }[];
};

export type JevNoulQuestion = {
  type: 'noul';
  instructions: string;
  criteria: { true: string; false: string };
};

export type JevScoreQuestion = {
  type: 'score';
  instructions: string;
  criteria: [string, ...string[]];
};

export type JevQuestion =
  | JevChoiceQuestion
  | JevNoulQuestion
  | JevScoreQuestion;

export type JevChoiceAnswer = {
  type: 'choice';
  choice: string;
  probabilities: Record<string, number>;
  confidence: number;
};

export type JevNoulAnswer = {
  type: 'noul';
  noul: number;
  confidence: number;
};

export type JevScoreAnswer = {
  type: 'score';
  score: number;
  confidence: number;
};

export type JevAnswer = JevChoiceAnswer | JevNoulAnswer | JevScoreAnswer;

type AnswerOf<Q extends JevQuestion> = Q extends JevChoiceQuestion
  ? JevChoiceAnswer
  : Q extends JevNoulQuestion
    ? JevNoulAnswer
    : Q extends JevScoreQuestion
      ? JevScoreAnswer
      : never;

export type JevAnswers<TQuestions extends Record<string, JevQuestion>> = {
  [K in keyof TQuestions]: AnswerOf<TQuestions[K]>;
};

export type JevDecisionsOptions = {
  apiKey?: string;
  model?: string;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
};

export type JevDecisionsClient = {
  decide: <TQuestions extends Record<string, JevQuestion>>(input: {
    state: string;
    questions: TQuestions;
  }) => Promise<JevAnswers<TQuestions>>;
  /** Usage object from the last successful decisions response, if present. */
  lastUsage: () => Record<string, unknown> | undefined;
};

const choiceAnswerSchema = z.object({
  type: z.literal('choice').optional(),
  choice: z.string(),
  probabilities: z.record(z.string(), z.number()).default({}),
  confidence: z.number().optional(),
});

const noulAnswerSchema = z.object({
  type: z.literal('noul').optional(),
  noul: z.number(),
  confidence: z.number().optional(),
});

const scoreAnswerSchema = z.object({
  type: z.literal('score').optional(),
  score: z.number(),
  confidence: z.number().optional(),
});

const wireAnswerSchema = z.union([
  choiceAnswerSchema,
  noulAnswerSchema,
  scoreAnswerSchema,
]);

const envelopeSchema = z.object({
  choices: z
    .array(z.object({ answers: z.record(z.string(), z.unknown()) }))
    .optional(),
  answers: z.record(z.string(), z.unknown()).optional(),
  usage: z.record(z.string(), z.unknown()).optional(),
});

const truncate = (body: string): string =>
  body.length <= BODY_LIMIT ? body : `${body.slice(0, BODY_LIMIT)}…`;

const readKey = (explicit: string | undefined): string => {
  const key = explicit ?? process.env.OPENROUTER_API_KEY;
  if (!key) {
    throw new AiException(AiError.MISSING_API_KEY.defaultMessage, {
      code: AiError.MISSING_API_KEY.code,
      provider: OPENROUTER_PROVIDER,
      env: 'OPENROUTER_API_KEY',
    });
  }
  return key;
};

const validateQuestions = (questions: Record<string, JevQuestion>): void => {
  for (const [name, question] of Object.entries(questions)) {
    if (question.type !== 'choice') continue;
    const missing = question.options
      .map((option) => option.label)
      .filter((label) => !(label in question.criteria));
    if (missing.length > 0) {
      throw new AiException(AiError.INVALID_DECISIONS_INPUT.defaultMessage, {
        code: AiError.INVALID_DECISIONS_INPUT.code,
        question: name,
        missingLabels: missing,
      });
    }
  }
};

const invalid = (field: string): never => {
  throw new AiException(AiError.INVALID_RESPONSE.defaultMessage, {
    code: AiError.INVALID_RESPONSE.code,
    field,
  });
};

const mapAnswer = (raw: unknown, field: string): JevAnswer => {
  const parsed = wireAnswerSchema.safeParse(raw);
  if (!parsed.success || parsed.data === undefined) {
    throw new AiException(AiError.INVALID_RESPONSE.defaultMessage, {
      code: AiError.INVALID_RESPONSE.code,
      field,
      received: JSON.stringify(raw).slice(0, 300),
    });
  }
  const value = parsed.data;
  if ('choice' in value && typeof value.choice === 'string') {
    const probabilities = value.probabilities ?? {};
    const peak = Math.max(0, ...Object.values(probabilities));
    return {
      type: 'choice',
      choice: value.choice,
      probabilities,
      confidence: value.confidence ?? peak,
    };
  }
  if ('noul' in value && typeof value.noul === 'number') {
    return {
      type: 'noul',
      noul: value.noul,
      confidence: value.confidence ?? value.noul,
    };
  }
  if ('score' in value && typeof value.score === 'number') {
    return {
      type: 'score',
      score: value.score,
      confidence: value.confidence ?? value.score,
    };
  }
  return invalid(field);
};

const answersFrom = (payload: unknown): Record<string, unknown> => {
  const envelope = envelopeSchema.safeParse(payload);
  if (envelope.success) {
    const nested = envelope.data.choices?.[0]?.answers ?? envelope.data.answers;
    if (nested) return nested;
  }
  const flat = z.record(z.string(), z.unknown()).safeParse(payload);
  if (flat.success) return flat.data;
  return invalid('answers');
};

export const createJevDecisions = (
  options: JevDecisionsOptions = {}
): JevDecisionsClient => {
  const model = options.model ?? DEFAULT_MODEL;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const fetchImpl = options.fetchImpl ?? fetch;
  let usage: Record<string, unknown> | undefined;

  return {
    lastUsage: () => usage,
    decide: async ({ state, questions }) => {
      validateQuestions(questions);
      const apiKey = readKey(options.apiKey);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const response = await fetchImpl(DECISIONS_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${apiKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ model, state, questions }),
          signal: controller.signal,
        });
        const raw = await response.text();
        if (!response.ok) {
          throw new AiException(AiError.DECISIONS_FAILED.defaultMessage, {
            code: AiError.DECISIONS_FAILED.code,
            status: response.status,
            body: truncate(raw),
          });
        }
        let payload: unknown;
        try {
          payload = JSON.parse(raw) as unknown;
        } catch {
          throw new AiException(AiError.INVALID_RESPONSE.defaultMessage, {
            code: AiError.INVALID_RESPONSE.code,
            body: truncate(raw),
          });
        }
        const envelope = envelopeSchema.safeParse(payload);
        if (envelope.success && envelope.data.usage) {
          usage = envelope.data.usage;
        }
        const wire = answersFrom(payload);
        const mapped = {} as Record<string, JevAnswer>;
        for (const name of Object.keys(questions)) {
          mapped[name] = mapAnswer(wire[name], name);
        }
        return mapped as JevAnswers<typeof questions>;
      } catch (error) {
        if (error instanceof AiException) throw error;
        if (controller.signal.aborted) {
          throw new AiException(AiError.DECISIONS_FAILED.defaultMessage, {
            code: AiError.DECISIONS_FAILED.code,
            timeoutMs,
          });
        }
        throw new AiException(AiError.DECISIONS_FAILED.defaultMessage, {
          code: AiError.DECISIONS_FAILED.code,
          cause: error instanceof Error ? error.message : String(error),
        });
      } finally {
        clearTimeout(timer);
      }
    },
  };
};
